<script type="text/jsx" lang="jsx">
import ConfigBase from '@/components/config-base';
import { BILLING_CYCLE, planTypes } from '@/common/constants';
import { getTrackingProductNameByPlanType } from '@/aspects/trackings';
import { getNewPlanUnitWithQuantity, planUnitMap, getSubscriptionCycleDesc } from '@/common/product-unit';
import { ServiceConfirm } from '@/components/common/service-dialog/confirm';
import PlanDetails from '@/plans/ZOOM_MEETING/dialog/plan-details';
import _ from 'lodash';
import { ZOOM_MEETING_SUB_TYPES } from '@/plans/ZOOM_MEETING/common/utils';
import { plansof } from '@/utils/chain';
import { mapGetters } from 'vuex';
import { computedPbxBasePlans, computedPbxTollFreePlans } from '@/common/service/computers/plans/pbx';
import { getZPCallingFeesText } from '@/plans/ZOOM_PHONE/helper/utils';
import { additionalConfigType, AdditionalConfigTypeEnums } from '@/plans/ZOOM_PHONE$ADDITIONAL/helper';
import { logger } from '@/utils/log';
import { getPlanCollapseCycleDesc } from '@/common/product';
import { getPlanLogo } from '@/common/plan-logo-map';

export default {
  name: 'ConfigProPlusBase',
  extends: ConfigBase,
  key: planTypes[planTypes.ZONE_PRO_PLUS],
  props: {
    showPlanDetails: Boolean,
    planDetailsLabel: String,
    hideComparisonTable: {
      type: Boolean,
      default: false,
    },
    showFullComparisonDialog: {
      type: Function,
      default: _.noop,
    },
  },
  data() {
    return {
      title: this.$t('billing.zone_pro_plus.title'),
      subtitle: '',
      planType: planTypes.ZONE_PRO_PLUS,
      logo: getPlanLogo(planTypes.ZONE_PRO_PLUS),
      showLearnMore: false,
      unit: planUnitMap.user,
      trackingPlanName: getTrackingProductNameByPlanType(planTypes.ZONE_PRO_PLUS),
    };
  },
  computed: {
    ...mapGetters(['getSelectedPlans', 'pbxBasePlansGroupByBillingCycle']),
    zoneProPlusPlanList() {
      return plansof(this.getSelectedPlans).type(this.planType).get();
    },
    cycleAndUsageFee() {
      const zoneProPlus = this.zoneProPlusPlanList?.[0];
      const pbxBasePlan = computedPbxBasePlans(this.getSelectedPlans)?.[0];
      return [zoneProPlus?.billing_cycle || BILLING_CYCLE.ANNUAL, pbxBasePlan?.plan_code];
    },
    cascadeCycleOptionList() {
      const cycleList = [BILLING_CYCLE.MONTHLY, BILLING_CYCLE.QUARTERLY, BILLING_CYCLE.ANNUAL, BILLING_CYCLE.TWO_YEARS, BILLING_CYCLE.THREE_YEARS];

      return _.map(cycleList, (cycle) => {
        return {
          value: cycle,
          options: _.map(this.pbxBasePlansGroupByBillingCycle[cycle], (plan) => {
            return {
              value: plan.plan_code,
              title: getZPCallingFeesText(plan).title,
              subTitle: this.$money(plan.amount, { forceDiff: true }),
              description: getZPCallingFeesText(plan).unit,
            };
          }),
        };
      });
    },
  },
  methods: {
    async showPlanDetailDialog() {
      // new UX in vue3 update
      // Check if showFullComparisonDialog was passed as prop (not using default _.noop)
      // Compare with _.noop reference to determine if prop was actually passed
      // Note: This works because _.noop is a singleton function from lodash
      if (this.showFullComparisonDialog !== _.noop) {
        return await this.showFullComparisonDialog();
      }

      if (this.$context.is.edit || this.$context.is.pro2prop) {
        return await this.showFullComparisonDialog();
      }
      this.$tracking.buyInteraction('plan details selected', this.trackingPlanName);
      try {
        await this.planDetailsDialog.confirm({ planCategory: ZOOM_MEETING_SUB_TYPES.PRO_PLUS, hideComparisonTable: this.hideComparisonTable });
        await this.showFullComparisonDialog().catch(()=>{
          // * in vue3 latest design, no need to switch between plan details and full comparison dialog
          // if(this.$context.is.modal){
          //   this.showPlanDetailDialog()
          // }
        })
      } catch (error) {
        logger.warn('showPlanDetailDialog', error);
      }
    },
    mx_renderProPlusCallingPlanLabel() {
      return _.map(this.zoneProPlusPlanList, (plan) => {
        const pbxCategoryCode = plan?.pbxCategory;
        if (!pbxCategoryCode) return '';

        const planName = this.$t(`billing.pbx.cart_name.${pbxCategoryCode}`);
        // const cycleLabel = getPlanUnitWithCycle(plan?.billing_cycle, this.unit);

        return <div class="opc-zm-base-card__collapse-pbx-item-row" key={plan.plan_code}>
          {planName} • {getNewPlanUnitWithQuantity(this.unit, plan.cartInfo.selected_number)} x {this.$money(plan.amount)} • {getPlanCollapseCycleDesc(plan.billing_cycle)}
        </div>;
      });
    },
    mx_renderPbxTollFreePlanLabel() {
      const selectedTFPlanList = computedPbxTollFreePlans(this.getSelectedPlans);

      return _.map(selectedTFPlanList, (plan) => {
        const cycleLabel = getPlanCollapseCycleDesc(plan.billing_cycle);
        const planName = this.$t(`billing.pbx.cart_name.${plan.category}`);

        return <div class="opc-zm-base-card__collapse-pbx-item-row" key={plan.plan_code}>
          {planName} • {getNewPlanUnitWithQuantity(planUnitMap.number, plan.cartInfo.selected_number)} x {this.$money(plan.amount)} • {cycleLabel}
        </div>;
      });
    },
    mx_renderPbxBasePlanLabel() {
      const [billingCycle, usageFeePlanCode] = this.cycleAndUsageFee;
      const cycleOption = _.find(this.cascadeCycleOptionList, (x) => x.value === billingCycle);
      const usageFeeOption = _.find(cycleOption?.options, (x) => x.value === usageFeePlanCode);
      if (!usageFeeOption) return null;
      // Check if it's prepaid option
      const isPrepaid = usageFeeOption.title === 'Prepaid';
      let usageFeeLabel;
      if (isPrepaid) {
        // For prepaid: remove description and add billing cycle subscription text
        const billingCycleText = getSubscriptionCycleDesc(billingCycle);
        usageFeeLabel = `${this.$t('billing.pbx.calling_fee_name.pay_you_go.MC')} • ${usageFeeOption.subTitle} • ${usageFeeOption.title} • ${billingCycleText}`;
      } else {
        // For pay as you go: keep original format
        usageFeeLabel = `${this.$t('billing.pbx.calling_fee_name.pay_you_go.MC')} • ${usageFeeOption.subTitle} ${usageFeeOption.description} • ${usageFeeOption.title}`;
      }

      return <div class="opc-zm-base-card__collapse-pbx-item-row">{usageFeeLabel}</div>;
    },
    mx_renderSelectedList() {
      if (this.isExpanded) return;

      return <div class="opc-zm-base-card__collapse opc-zm-base-card__collapse--reset" slot="collapse">
        {this.mx_renderProPlusCallingPlanLabel()}
        {additionalConfigType.configType === AdditionalConfigTypeEnums.PN_AND_TF ? null : this.mx_renderPbxTollFreePlanLabel()}
        {this.mx_renderPbxBasePlanLabel()}
      </div>;
    },
  },
  created() {
    this.planDetailsDialog = new ServiceConfirm(PlanDetails);
  },
};
</script>
